import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  ChatBubbleOutline as ChatBubbleIcon,
  Delete as DeletedIcon,
  Restore as RestoredIcon,
  SummarizeOutlined as SummarizeIcon,
} from '@mui/icons-material';
import { CommonTablePagination, ExternalLinkButton, NameNicknameDisplay, TimeDisplay } from '../../common';
import { ChatMessageDeletionHistory, getRoomTypeLabel } from '../../../types/chatMessageDeletionHistory';
import { MESSAGE_DISPLAY_LIMIT, TABLE_THEME } from '../../../types/common';
import { getExternalAdminTalkRoomUrl, getExternalSeniorTalkRoomUrl, getExternalTalkRoomUrl } from '../../../utils/urlUtils';
import UserRoleChip from './UserRoleChip';

interface DeletionHistoryTableProps {
  histories: ChatMessageDeletionHistory[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  loading: boolean;
  isDeveloper: boolean;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const DeletionHistoryTable: React.FC<DeletionHistoryTableProps> = ({
  histories,
  totalCount,
  page,
  rowsPerPage,
  loading,
  isDeveloper,
  onPageChange,
  onRowsPerPageChange,
}) => {
  // メッセージを短縮表示する関数
  const formatMessage = (message: string): string => {
    if (!message) return '';
    if (message.length <= MESSAGE_DISPLAY_LIMIT) return message;
    return `${message.substring(0, MESSAGE_DISPLAY_LIMIT)}...`;
  };
  // ルームタイプに応じた外部アプリURLを生成する関数
  const getExternalRoomUrl = (history: ChatMessageDeletionHistory): string | null => {
    if (!history.room_id) return null;
    switch (history.room_type) {
      case 'TalkRoom':
        return getExternalTalkRoomUrl(history.room_id);
      case 'AdminTalkRoom':
        return getExternalAdminTalkRoomUrl(history.room_id);
      case 'SeniorTalkRoom':
        return getExternalSeniorTalkRoomUrl(history.room_id);
      default:
        return null;
    }
  };
  return (
    <Paper sx={{ width: '100%', mt: 2 }}>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="チャットメッセージ削除履歴">
          <TableHead>
            <TableRow>
              <TableCell sx={{
                fontWeight: 'bold',
                backgroundColor: TABLE_THEME.HEADER.BACKGROUND,
                borderBottom: `2px solid ${TABLE_THEME.HEADER.BORDER}`,
                color: TABLE_THEME.HEADER.TEXT
              }}>操作</TableCell>
              <TableCell sx={{
                fontWeight: 'bold',
                backgroundColor: TABLE_THEME.HEADER.BACKGROUND,
                borderBottom: `2px solid ${TABLE_THEME.HEADER.BORDER}`,
                color: TABLE_THEME.HEADER.TEXT
              }}>操作日時</TableCell>
              <TableCell sx={{
                fontWeight: 'bold',
                backgroundColor: TABLE_THEME.HEADER.BACKGROUND,
                borderBottom: `2px solid ${TABLE_THEME.HEADER.BORDER}`,
                color: TABLE_THEME.HEADER.TEXT
              }}>ルーム</TableCell>
              <TableCell sx={{
                fontWeight: 'bold',
                backgroundColor: TABLE_THEME.HEADER.BACKGROUND,
                borderBottom: `2px solid ${TABLE_THEME.HEADER.BORDER}`,
                color: TABLE_THEME.HEADER.TEXT
              }}>メッセージ</TableCell>
              <TableCell sx={{
                fontWeight: 'bold',
                backgroundColor: TABLE_THEME.HEADER.BACKGROUND,
                borderBottom: `2px solid ${TABLE_THEME.HEADER.BORDER}`,
                color: TABLE_THEME.HEADER.TEXT
              }}>親ユーザー</TableCell>
              <TableCell sx={{
                fontWeight: 'bold',
                backgroundColor: TABLE_THEME.HEADER.BACKGROUND,
                borderBottom: `2px solid ${TABLE_THEME.HEADER.BORDER}`,
                color: TABLE_THEME.HEADER.TEXT
              }}>子ユーザー</TableCell>
              <TableCell sx={{
                fontWeight: 'bold',
                backgroundColor: TABLE_THEME.HEADER.BACKGROUND,
                borderBottom: `2px solid ${TABLE_THEME.HEADER.BORDER}`,
                color: TABLE_THEME.HEADER.TEXT
              }} align="center">チャット</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress/>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    データを読み込み中...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : histories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography variant="body1">
                    削除履歴データがありません
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              histories.map((history) => (
                <TableRow key={history.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {history.deleted_at && (
                        <Chip
                          icon={<DeletedIcon/>}
                          label="削除"
                          size="small"
                          color="error"
                          variant={history.restored_at ? "outlined" : "filled"}
                        />
                      )}
                      {history.restored_at && (
                        <Chip
                          icon={<RestoredIcon/>}
                          label="復元"
                          size="small"
                          color="success"
                          variant="filled"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {history.deleted_at ? (
                      <>
                        <TimeDisplay timestamp={history.deleted_at}/>
                        <Box sx={{ mt: 0.5 }}>
                          {/* deleted_by_uidがあり、parent_uidとchild_uidのどちらにもマッチしない場合はオーナー表示 */}
                          {history.deleted_by_uid &&
                          history.deleted_by_uid !== history.parent_uid &&
                          history.deleted_by_uid !== history.child_uid ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.7rem' }}>
                                by
                              </Typography>
                              <Tooltip
                                title={history.deleted_user ?
                                  `${history.deleted_user.name}${history.deleted_user.nickname ? ` / ${history.deleted_user.nickname}` : ''}` :
                                  ''}
                                arrow
                                placement="top"
                              >
                                <Chip
                                  label="オーナー"
                                  size="small"
                                  sx={{
                                    height: '16px',
                                    fontSize: '0.65rem',
                                    backgroundColor: '#f3e5f5', // 薄い紫
                                    color: '#9c27b0', // 濃い紫（オーナー色）
                                    border: '1px solid #e1bee7', // 紫のボーダー
                                    borderRadius: '16px',
                                    fontWeight: 'normal',
                                    cursor: 'pointer'
                                  }}
                                />
                              </Tooltip>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.7rem' }}>
                                by
                              </Typography>
                              <Tooltip
                                title={history.deleted_user ?
                                  `${history.deleted_user.name}${history.deleted_user.nickname ? ` / ${history.deleted_user.nickname}` : ''}` :
                                  ''}
                                arrow
                                placement="top"
                              >
                                <Chip
                                  label={history.deleted_by_uid === history.parent_uid
                                    ? `親: ${history.deleted_user_type === 'nurse' ? '看護師' : '患者'}`
                                    : `${history.deleted_user_type === 'nurse' ? '看護師' : '患者'}`}
                                  size="small"
                                  sx={{
                                    height: '16px',
                                    fontSize: '0.65rem',
                                    backgroundColor: history.deleted_user_type === 'nurse' ? '#ffeef3' : '#e8f5e9',
                                    color: history.deleted_user_type === 'nurse' ? '#f50057' : '#4CAF50',
                                    border: `1px solid ${history.deleted_user_type === 'nurse' ? '#ffcdd2' : '#c8e6c9'}`,
                                    borderRadius: history.deleted_by_uid === history.parent_uid && history.deleted_user_type === 'nurse' ? '4px' : '16px',
                                    fontWeight: 'normal',
                                    cursor: 'pointer'
                                  }}
                                />
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      </>
                    ) : history.restored_at ? (
                      <>
                        <TimeDisplay timestamp={history.restored_at}/>
                        <Box sx={{ mt: 0.5 }}>
                          {/* restored_by_uidがある場合は必ずオーナー表示 */}
                          {history.restored_by_uid ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.7rem' }}>
                                by
                              </Typography>
                              <Tooltip
                                title={history.restored_user ?
                                  `${history.restored_user.name}${history.restored_user.nickname ? ` / ${history.restored_user.nickname}` : ''}` :
                                  ''}
                                arrow
                                placement="top"
                              >
                                <Chip
                                  label="オーナー"
                                  size="small"
                                  sx={{
                                    height: '16px',
                                    fontSize: '0.65rem',
                                    backgroundColor: '#f3e5f5', // 薄い紫
                                    color: '#9c27b0', // 濃い紫（オーナー色）
                                    border: '1px solid #e1bee7', // 紫のボーダー
                                    borderRadius: '16px',
                                    fontWeight: 'normal',
                                    cursor: 'pointer'
                                  }}
                                />
                              </Tooltip>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.7rem' }}>
                                by
                              </Typography>
                              <UserRoleChip
                                userInfo={history.restored_user}
                                userType={history.restored_user_type || 'nurse'}
                                size="small"
                              />
                            </Box>
                          )}
                        </Box>
                      </>
                    ) : (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: 0, // パディングを削除してリンクが全体を覆うようにする
                      cursor: history.room_type === 'TalkRoom' && history.parent_user && history.child_user ? 'pointer' : 'default'
                    }}
                  >
                    {history.room_type === 'TalkRoom' && history.parent_user && history.child_user ? (
                      <Link
                        to={`/talk_rooms/summary?nurse_id=${history.parent_user.id}&patient_id=${history.child_user.id}`}
                        style={{
                          textDecoration: 'none',
                          color: 'inherit',
                          display: 'block',
                          width: '100%',
                          height: '100%',
                          padding: '16px', // 元のセルのパディングを復元
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            p: 0.5,
                            ml: -0.5,
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.08)',
                              '& .room-label': {
                                color: 'primary.main',
                              },
                              '& .summary-icon': {
                                opacity: 1
                              }
                            }
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            className="room-label"
                            sx={{ transition: 'color 0.2s ease' }}
                          >
                            {getRoomTypeLabel(history.room_type)}
                          </Typography>
                          <SummarizeIcon
                            fontSize="small"
                            sx={{ ml: 0.5, opacity: 0.7, transition: 'opacity 0.2s ease' }}
                            color="primary"
                            className="summary-icon"
                          />
                        </Box>
                      </Link>
                    ) : (
                      <Box sx={{ padding: '16px' }}>
                        <Typography variant="body2" fontWeight="medium">
                          {getRoomTypeLabel(history.room_type)}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {history.message ? (
                      history.message.length > MESSAGE_DISPLAY_LIMIT ? (
                        // 制限文字数を超える場合はTooltipを表示
                        <Tooltip
                          title={history.message}
                          arrow
                          placement="top"
                          enterDelay={500}
                          sx={{ maxWidth: 500 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.85rem',
                              color: 'text.primary',
                              cursor: 'help'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {`${history.message.substring(0, MESSAGE_DISPLAY_LIMIT)}...`}
                              <ChatBubbleIcon sx={{ ml: 0.5, fontSize: '0.9rem', color: 'text.secondary', opacity: 0.7 }}/>
                            </Box>
                          </Typography>
                        </Tooltip>
                      ) : (
                        // 制限文字数以内の場合はTooltipなし
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.85rem',
                            color: 'text.primary'
                          }}
                        >
                          {history.message}
                        </Typography>
                      )
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.85rem' }}>
                        メッセージなし
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="caption" color="textSecondary">
                        <TimeDisplay timestamp={history.sent_at}/>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontSize: '0.7rem' }}>
                        by
                      </Typography>
                      <Chip
                        label={
                          history.send_user_uid === history.parent_uid
                            ? `親: ${history.send_user_type === 'nurse' ? '看護師' : '患者'}`
                            : history.send_user_type === 'nurse' ? '看護師' : '患者'
                        }
                        size="small"
                        sx={{
                          height: '16px',
                          fontSize: '0.65rem',
                          ml: 0.5,
                          backgroundColor: history.send_user_type === 'nurse' ? '#ffeef3' : '#e8f5e9',
                          color: history.send_user_type === 'nurse' ? '#f50057' : '#4CAF50',
                          border: `1px solid ${history.send_user_type === 'nurse' ? '#ffcdd2' : '#c8e6c9'}`,
                          borderRadius: history.send_user_uid === history.parent_uid && history.send_user_type === 'nurse' ? '4px' : '16px',
                          fontWeight: 'normal'
                        }}
                      />
                    </Box>
                    {isDeveloper && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        ID: {history.message_id}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: 0,
                      cursor: history.parent_user ? 'pointer' : 'default'
                    }}
                  >
                    {history.parent_user ? (
                      <Link
                        to={history.parent_user_type === 'nurse'
                          ? `/nurses/${history.parent_user.id}`
                          : `/patients/${history.parent_user.id}`
                        }
                        style={{
                          textDecoration: 'none',
                          color: 'inherit',
                          display: 'block',
                          width: '100%',
                          height: '100%',
                          padding: '16px'
                        }}
                      >
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          borderRadius: '4px',
                          transition: 'all 0.2s ease',
                          p: 0.5,
                          ml: -0.5,
                          '&:hover': {
                            backgroundColor: history.parent_user_type === 'nurse'
                              ? 'rgba(245, 0, 87, 0.08)' // 看護師用の背景色（薄いピンク）
                              : 'rgba(76, 175, 80, 0.08)', // 患者用の背景色（薄い緑）
                            '& .parent-chip': {
                              borderColor: history.parent_user_type === 'nurse' ? '#ff4081' : '#66bb6a',
                              backgroundColor: history.parent_user_type === 'nurse' ? '#fff1f4' : '#f1f8f1'
                            },
                            '& .parent-name': {
                              color: history.parent_user_type === 'nurse' ? '#f50057' : '#4CAF50'
                            }
                          }
                        }}>
                          <Chip
                            label={`親: ${history.parent_user_type === 'nurse' ? '看護師' : '患者'}`}
                            size="small"
                            className="parent-chip"
                            sx={{
                              height: '20px',
                              fontSize: '0.65rem',
                              backgroundColor: history.parent_user_type === 'nurse' ? '#ffeef3' : '#e8f5e9',
                              color: history.parent_user_type === 'nurse' ? '#f50057' : '#4CAF50',
                              border: `1px solid ${history.parent_user_type === 'nurse' ? '#ffcdd2' : '#c8e6c9'}`,
                              borderRadius: history.parent_user_type === 'nurse' ? '4px' : '16px',
                              fontWeight: 'normal',
                              mr: 1,
                              mt: 0.3,
                              transition: 'all 0.2s ease'
                            }}
                          />
                          <Box className="parent-name" sx={{ transition: 'color 0.2s ease' }}>
                            <NameNicknameDisplay
                              name={history.parent_user.name}
                              nickname={history.parent_user.nickname}
                              nameFontSize="0.85rem"
                              nicknameFontSize="0.75rem"
                              spacing={0.2}
                              nameHoverClass="hover-name"
                              nicknameHoverClass="hover-name"
                              isTestAccount={(history.parent_user as any)?.is_test_account || false}
                            />
                          </Box>
                        </Box>
                      </Link>
                    ) : (
                      <Box sx={{ padding: '16px', display: 'flex', alignItems: 'flex-start' }}>
                        <Chip
                          label={`親: ${history.parent_user_type === 'nurse' ? '看護師' : '患者'}`}
                          size="small"
                          sx={{
                            height: '20px',
                            fontSize: '0.65rem',
                            backgroundColor: history.parent_user_type === 'nurse' ? '#ffeef3' : '#e8f5e9',
                            color: history.parent_user_type === 'nurse' ? '#f50057' : '#4CAF50',
                            border: `1px solid ${history.parent_user_type === 'nurse' ? '#ffcdd2' : '#c8e6c9'}`,
                            borderRadius: history.parent_user_type === 'nurse' ? '4px' : '16px',
                            fontWeight: 'normal',
                            mr: 1,
                            mt: 0.3
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: 0,
                      cursor: history.child_user ? 'pointer' : 'default'
                    }}
                  >
                    {history.child_user ? (
                      <Link
                        to={history.child_user_type === 'nurse'
                          ? `/nurses/${history.child_user.id}`
                          : `/patients/${history.child_user.id}`
                        }
                        style={{
                          textDecoration: 'none',
                          color: 'inherit',
                          display: 'block',
                          width: '100%',
                          height: '100%',
                          padding: '16px'
                        }}
                      >
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          borderRadius: '4px',
                          transition: 'all 0.2s ease',
                          p: 0.5,
                          ml: -0.5,
                          '&:hover': {
                            backgroundColor: history.child_user_type === 'nurse'
                              ? 'rgba(245, 0, 87, 0.08)' // 看護師用の背景色（薄いピンク）
                              : 'rgba(76, 175, 80, 0.08)', // 患者用の背景色（薄い緑）
                            '& .child-chip': {
                              borderColor: history.child_user_type === 'nurse' ? '#ff4081' : '#66bb6a',
                              backgroundColor: history.child_user_type === 'nurse' ? '#fff1f4' : '#f1f8f1'
                            },
                            '& .child-name': {
                              color: history.child_user_type === 'nurse' ? '#f50057' : '#4CAF50'
                            }
                          }
                        }}>
                          <Chip
                            label={`${history.child_user_type === 'nurse' ? '看護師' : '患者'}`}
                            size="small"
                            className="child-chip"
                            sx={{
                              height: '20px',
                              fontSize: '0.65rem',
                              backgroundColor: history.child_user_type === 'nurse' ? '#ffeef3' : '#e8f5e9',
                              color: history.child_user_type === 'nurse' ? '#f50057' : '#4CAF50',
                              border: `1px solid ${history.child_user_type === 'nurse' ? '#ffcdd2' : '#c8e6c9'}`,
                              borderRadius: '16px',
                              fontWeight: 'normal',
                              mr: 1,
                              mt: 0.3,
                              transition: 'all 0.2s ease'
                            }}
                          />
                          <Box className="child-name" sx={{ transition: 'color 0.2s ease' }}>
                            <NameNicknameDisplay
                              name={history.child_user.name}
                              nickname={history.child_user.nickname}
                              nameFontSize="0.85rem"
                              nicknameFontSize="0.75rem"
                              spacing={0.2}
                              nameHoverClass="hover-name"
                              nicknameHoverClass="hover-name"
                              isTestAccount={(history.child_user as any)?.is_test_account || false}
                            />
                          </Box>
                        </Box>
                      </Link>
                    ) : (
                      <Box sx={{ padding: '16px', display: 'flex', alignItems: 'flex-start' }}>
                        <Chip
                          label={`${history.child_user_type === 'nurse' ? '看護師' : '患者'}`}
                          size="small"
                          sx={{
                            height: '20px',
                            fontSize: '0.65rem',
                            backgroundColor: history.child_user_type === 'nurse' ? '#ffeef3' : '#e8f5e9',
                            color: history.child_user_type === 'nurse' ? '#f50057' : '#4CAF50',
                            border: `1px solid ${history.child_user_type === 'nurse' ? '#ffcdd2' : '#c8e6c9'}`,
                            borderRadius: '16px',
                            fontWeight: 'normal',
                            mr: 1,
                            mt: 0.3
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {getExternalRoomUrl(history) && (
                      <ExternalLinkButton
                        href={getExternalRoomUrl(history) || '#'}
                        size="small"
                        buttonType="chat"
                        tooltip={<>
                          アプリ(Web)でチャットを表示
                          {isDeveloper && (
                            <>
                              <br/>
                              チャットルームID: {history.chatroom_id}
                            </>
                          )}
                        </>}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CommonTablePagination
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Paper>
  );
};
export default DeletionHistoryTable;
